import { useEffect, useState, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'

/*
 * Company payment-method management (inventory §5.3). List saved cards (set
 * default / delete) and add a new one via Stripe Elements:
 *   GET /billing/payment-method  ·  GET /billing/setup-intent
 *   POST /billing/payment-method · PUT /billing/payment-method/default
 *   DELETE /billing/payment-method/:id
 */
export default function PaymentMethodView({ t: tProp, onBack }) {
  const { t } = useTranslation()
  const _t = tProp || t
  const { confirm, toast, notify } = useFeedback()
  const { i18n } = useTranslation()
  const [data, setData] = useState(null) // { publishable_key, payment_methods }
  const [stripePromise, setStripePromise] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(() => {
    api.get('/billing/payment-method').then(({ data: d }) => {
      setData(d)
      if (d?.publishable_key) setStripePromise(loadStripe(d.publishable_key, { locale: i18n.resolvedLanguage }))
    }).catch(() => setData({ payment_methods: [] }))
  }, [i18n.resolvedLanguage])
  useEffect(() => { load() }, [load])

  const setDefault = async (pm) => {
    setBusyId(pm.id)
    try { await api.put('/billing/payment-method/default', { payment_method_id: pm.id }); toast(_t('paymentMethod.defaultSet'), 'success'); load() }
    catch (e) { notify({ tone: 'error', title: _t('settings.error'), message: e?.response?.data?.error || _t('settings.errorGeneric') }) } finally { setBusyId(null) }
  }
  const remove = async (pm) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: _t('paymentMethod.confirmDelete'), message: `•••• ${pm.card?.last4 || ''}`, confirmLabel: _t('common.delete'), cancelLabel: _t('common.cancel') })
    if (!ok) return
    setBusyId(pm.id)
    try { await api.delete(`/billing/payment-method/${pm.id}`); toast(_t('paymentMethod.deleted'), 'success'); load() }
    catch (e) { notify({ tone: 'error', title: _t('settings.error'), message: e?.response?.data?.error || _t('settings.errorGeneric') }) } finally { setBusyId(null) }
  }

  const methods = data?.payment_methods || []

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <Button variant="secondary" onClick={onBack}>{_t('common.back')}</Button>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16 }}>{_t('paymentMethod.title')}</h3>
      </div>

      {data == null ? <div style={{ color: 'var(--color-text-muted)' }}>{_t('common.loading')}</div> : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
            {methods.length === 0 && <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5 }}>{_t('paymentMethod.empty')}</div>}
            {methods.map((pm) => (
              <div key={pm.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: 12 }}>
                <Icon name="credit-card" size={20} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-ink)', textTransform: 'capitalize' }}>{pm.card?.brand} •••• {pm.card?.last4}{pm.is_default && <span style={{ marginLeft: 8 }}><Badge tone="info">{_t('paymentMethod.default')}</Badge></span>}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{_t('paymentMethod.expires')} {String(pm.card?.exp_month).padStart(2, '0')}/{pm.card?.exp_year}</div>
                </div>
                {!pm.is_default && <Button variant="secondary" disabled={busyId === pm.id} onClick={() => setDefault(pm)}>{_t('paymentMethod.setDefault')}</Button>}
                {methods.length > 1 && <IconButton icon="trash" variant="danger" title={_t('common.delete')} disabled={busyId === pm.id} onClick={() => remove(pm)} />}
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>{_t('paymentMethod.addCard')}</div>
          {stripePromise
            ? <Elements stripe={stripePromise} options={{ locale: i18n.resolvedLanguage }}><AddCardForm t={_t} onAdded={load} /></Elements>
            : <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5 }}>{_t('paymentMethod.stripeUnavailable')}</div>}
        </>
      )}
    </Card>
  )
}

function AddCardForm({ t, onAdded }) {
  const stripe = useStripe()
  const elements = useElements()
  const { toast } = useFeedback()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true); setError('')
    try {
      const { data } = await api.get('/billing/setup-intent')
      const { error: stripeErr, setupIntent } = await stripe.confirmCardSetup(data.client_secret, { payment_method: { card: elements.getElement(CardElement) } })
      if (stripeErr) { setError(stripeErr.message || t('paymentMethod.cardError')); return }
      await api.post('/billing/payment-method', { payment_method_id: setupIntent.payment_method })
      toast(t('paymentMethod.added'), 'success')
      elements.getElement(CardElement)?.clear()
      onAdded()
    } catch (err) { setError(err?.response?.data?.error || t('paymentMethod.cardError')) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit}>
      <div style={{ border: '1.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-field)', padding: '14px', marginBottom: 12 }}>
        <CardElement options={{ style: { base: { fontSize: '15px', color: '#2A1A4A', '::placeholder': { color: '#9a93ad' } } } }} />
      </div>
      {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-muted)', fontSize: 12.5, marginBottom: 14 }}><Icon name="shield" size={15} /> {t('paymentMethod.secure')}</div>
      <Button type="submit" variant="primary" icon="plus" loading={busy} disabled={!stripe}>{t('paymentMethod.addCardCta')}</Button>
    </form>
  )
}
